<template>
  <ErrorBoundary>
    <div class="ephemera-archive">
      <!-- Initialization Sequence -->
      <Transition name="init-fade" @after-leave="showMain = true">
        <InitializationSequence
          v-if="!initialized"
          @complete="onInitializationComplete"
        />
      </Transition>

      <!-- Main Application -->
      <Transition name="main-enter">
        <div v-if="showMain" class="main-shell">
          <Header />
          <Navigation />
          <main class="content">
            <router-view v-slot="{ Component }">
              <Transition name="page-fade" mode="out-in">
                <component :is="Component" />
              </Transition>
            </router-view>
          </main>

          <Footer />

          <!-- Achievement Notifications (Phase 3) -->
          <div class="notifications-container">
            <TransitionGroup name="notification" tag="div">
              <AchievementNotification
                v-for="notification in archiveStore.achievementNotifications"
                :key="notification.id"
                :achievement="notification"
                @close="
                  archiveStore.removeAchievementNotification(notification.id)
                "
              />
            </TransitionGroup>
          </div>

          <!-- Degraded Mode Warning -->
          <Transition name="slide-down">
            <div v-if="archiveStore.sseDegraded" class="degraded-banner">
              🤗 The archive is experiencing a traffic hug! Real-time updates
              may be delayed.
            </div>
          </Transition>
        </div>
      </Transition>
    </div>
  </ErrorBoundary>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import Header from "./components/layout/Header.vue";
import Navigation from "./components/layout/Navigation.vue";
import Footer from "./components/layout/Footer.vue";
import InitializationSequence from "./components/InitializationSequence.vue";
import AchievementNotification from "./components/AchievementNotification.vue";
import ErrorBoundary from "./components/ErrorBoundary.vue";
import { useArchiveStore } from "./stores/archive";

const hasPlayedInit = sessionStorage.getItem("ephemera-init-played") === "1";
const initialized = ref(hasPlayedInit);
const showMain = ref(hasPlayedInit);

const archiveStore = useArchiveStore();

onMounted(() => {
  archiveStore.initialize();
});

function onInitializationComplete() {
  sessionStorage.setItem("ephemera-init-played", "1");
  initialized.value = true;
}
</script>

<style>
/* Global Museum Aesthetic Styles */
:root {
  /* Museum Color Palette */
  --color-sepia-light: #f5f1e8;
  --color-sepia-medium: #e8dcc8;
  --color-black-primary: #1a1a1a;
  --color-black-secondary: #2d2d2d;
  --color-burgundy: #8b1a1a;
  --color-museum-red: #a52a2a;

  /* Rarity Colors */
  --color-common: #9ca3af;
  --color-uncommon: #5b7a9d;
  --color-rare: #8b5ca8;
  --color-legendary: #d4af37;

  /* Typography */
  --font-serif: "Georgia", "Times New Roman", serif;
  --font-sans: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;

  /* Semantic Text Colors */
  --color-text-muted: #6b6b6b;
  --color-text-disabled: #999;

  /* Surface Colors */
  --color-surface-primary: #ffffff;
  --color-surface-elevated: #fffef7;

  /* Rarity Tints */
  --color-common-tint: #f5f5f5;
  --color-uncommon-tint: #edf2f7;
  --color-rare-tint: #f3edf7;
  --color-legendary-tint: #fdf8e8;

  /* Rarity Glows */
  --glow-uncommon: rgba(91, 122, 157, 0.3);
  --glow-rare: rgba(139, 92, 168, 0.35);
  --glow-legendary: rgba(212, 175, 55, 0.4);

  /* Animation Tokens */
  --duration-fast: 0.15s;
  --duration-normal: 0.3s;
  --duration-slow: 0.5s;
  --duration-reveal: 0.8s;
  --ease-out-expo: cubic-bezier(0.16, 1, 0.3, 1);
  --ease-spring: cubic-bezier(0.34, 1.56, 0.64, 1);

  /* Z-Index Scale */
  --z-sticky: 200;
  --z-notification: 1000;

  /* Spacing */
  --spacing-xs: 0.25rem;
  --spacing-sm: 0.5rem;
  --spacing-md: 1rem;
  --spacing-lg: 1.5rem;
  --spacing-xl: 2rem;
  --spacing-2xl: 3rem;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  font-family: var(--font-sans);
  background-color: var(--color-sepia-light);
  color: var(--color-black-primary);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

h1,
h2,
h3,
h4,
h5,
h6 {
  font-family: var(--font-serif);
  font-weight: 600;
  color: var(--color-black-primary);
}

.ephemera-archive {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.content {
  flex: 1;
  padding: var(--spacing-xl);
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

/* Utility Classes */
.text-burgundy {
  color: var(--color-burgundy);
}

.text-museum-red {
  color: var(--color-museum-red);
}

.rarity-common {
  color: var(--color-common);
}

.rarity-uncommon {
  color: var(--color-uncommon);
}

.rarity-rare {
  color: var(--color-rare);
}

.rarity-legendary {
  color: var(--color-legendary);
}

/* Achievement Notifications Container */
.notifications-container {
  position: fixed;
  top: var(--spacing-xl);
  right: var(--spacing-xl);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  max-width: 450px;
}

/* Notification Transitions */
.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100px);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100px) scale(0.8);
}

.notification-move {
  transition: transform 0.3s ease;
}

/* Degraded Mode Banner */
.degraded-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: var(--color-museum-red);
  color: white;
  padding: var(--spacing-md);
  text-align: center;
  font-weight: 600;
  z-index: 999;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Slide-down transition for degraded banner */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-100%);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}

/* Init-to-main crossfade */
.init-fade-leave-active {
  transition: opacity 0.4s ease;
}
.init-fade-leave-to {
  opacity: 0;
}

.main-enter-enter-active {
  transition: opacity 0.6s var(--ease-out-expo);
}
.main-enter-enter-from {
  opacity: 0;
}

.main-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* Page transitions - asymmetric */
.page-fade-leave-active {
  transition: opacity var(--duration-fast) ease;
}
.page-fade-enter-active {
  transition:
    opacity var(--duration-slow) var(--ease-out-expo),
    transform var(--duration-slow) var(--ease-out-expo);
}

.page-fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.page-fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .content {
    padding: var(--spacing-md);
  }

  .notifications-container {
    top: var(--spacing-md);
    right: var(--spacing-md);
    left: var(--spacing-md);
    max-width: none;
  }
}

/* Reduced Motion Global Override */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
</style>
